import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  Chip,
  Tooltip,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  IconButton
} from '@mui/material';
import { 
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  AccessTime as TimeIcon,
  LockOutlined as LockIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import * as resourceService from '../services/resourceService';
import eventEmitter, { EVENTS } from '../utils/eventEmitter';
import { useAuth } from '../contexts/AuthContext';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Predefined resource types and their associated options
const RESOURCE_TYPES = {
  'Studio': ['Studio A', 'Studio B', 'Studio C'],
  'Equipment': ['Camera', 'Lighting', 'Sound', 'Grip'],
  'Post-Production': ['Editing Suite', 'Color Grading', 'Sound Mixing'],
  'Meeting Room': ['Conference Room A', 'Conference Room B', 'Screening Room'],
  'Hair & Makeup': ['Makeup Station', 'Hair Station', 'Prosthetics'],
  'Wardrobe': ['Costume Room', 'Fitting Room', 'Storage'],
  'Custom': ['Other'],
  'Vehicles': ['Truck', 'Car', 'Van'],
  'Props': ['Props Room', 'Props Storage'],
  'Set Dressing': ['Set A', 'Set B', 'Set C'],
  'Catering': ['Craft Services', 'Meals']
};

const RESOURCE_DETAILS = {
  'Studio': {
    icon: '🎬',
    fields: [],
    description: 'Production and recording spaces'
  },
  'Equipment': {
    icon: '🎥',
    fields: ['model', 'serialNumber', 'purchaseDate', 'warranty'],
    description: 'Production equipment and gear'
  },
  'Post-Production': {
    icon: '🖥️',
    fields: ['software', 'specifications', 'licenses'],
    description: 'Post-production facilities and software'
  },
  'Meeting Room': {
    icon: '🏢',
    fields: ['capacity', 'amenities', 'location'],
    description: 'Meeting and presentation spaces'
  },
  'Hair & Makeup': {
    icon: '💄',
    fields: ['capacity', 'amenities', 'location'],
    description: 'Hair and makeup facilities'
  },
  'Wardrobe': {
    icon: '👗',
    fields: ['capacity', 'amenities', 'location'],
    description: 'Wardrobe and costume facilities'
  },
  
  'Custom': {
    icon: '📦',
    fields: ['specifications', 'details'],
    description: 'Custom resource type'
  },
  'Vehicles': {
    icon: '🚐',
    fields: ['make', 'model', 'plate', 'driver', 'fuelType'],
    description: 'Production vehicles and transportation'
  },
  'Props': {
    icon: '🎭',
    fields: ['propMaster', 'condition', 'acquisitionDate', 'value'],
    description: 'Props inventory tracking'
  },
  'Set Dressing': {
    icon: '🖼️',
    fields: ['setDecorator', 'scene', 'requirements', 'status'],
    description: 'Set dressing requirements'
  },
  'Catering': {
    icon: '🍽️',
    fields: ['vendor', 'meals', 'dietaryRestrictions', 'contact'],
    description: 'Craft services and meals'
  },
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  // Let PaperProps control visuals for consistency with EditTaskModal
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.25rem',
  padding: theme.spacing(2)
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3)
}));

const StyledFormField = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
  },
  marginBottom: theme.spacing(2)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius
}));

const ResourceEditModal = ({ open, onClose, resource, onResourceUpdate, projectId }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    subtype: '',
    status: 'pending_approval',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    allDay: true,
    startTime: '09:00',
    endTime: '17:00',
    location: '',
    capacity: '',
    equipment: '',
    model: '',
    serialNumber: '',
    purchaseDate: '',
    warranty: '',
    software: '',
    specifications: '',
    licenses: '',
    amenities: '',
    details: '',
    notes: '',
    make: '',
    plate: '',
    driver: '',
    fuelType: '',
    propMaster: '',
    condition: '',
    acquisitionDate: '',
    value: '',
    setDecorator: '',
    scene: '',
    requirements: '',
    vendor: '',
    meals: '',
    dietaryRestrictions: '',
    contact: '',
    tags: []
  });
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if user is a superuser (only Nathan McConnell for now)
  const isSuperuser = user && user.email === 'nathan.mcconnell@sandboxentmt.com';

  useEffect(() => {
    if (resource) {
      const toDateOnly = (v) => (v ? String(v).slice(0, 10) : new Date().toISOString().split('T')[0]);
      const toTimeOnly = (v) => {
        if (!v) return '';
        const s = String(v);
        if (s.includes('T')) return s.slice(11, 16);
        if (/^\d{2}:\d{2}$/.test(s)) return s;
        return '';
      };
      setFormData({
        name: resource.name || '',
        type: resource.type || '',
        subtype: resource.subtype || '',
        status: resource.status || 'pending_approval',
        startDate: toDateOnly(resource.startDate),
        endDate: resource.endDate ? toDateOnly(resource.endDate) : toDateOnly(resource.startDate),
        allDay: resource.allDay !== undefined ? !!resource.allDay : true,
        startTime: toTimeOnly(resource.startTime) || '09:00',
        endTime: toTimeOnly(resource.endTime) || '17:00',
        location: resource.location || '',
        capacity: resource.capacity || '',
        equipment: resource.equipment || '',
        model: resource.model || '',
        serialNumber: resource.serialNumber || '',
        purchaseDate: resource.purchaseDate || '',
        warranty: resource.warranty || '',
        software: resource.software || '',
        specifications: resource.specifications || '',
        licenses: resource.licenses || '',
        amenities: resource.amenities || '',
        details: resource.details || '',
        notes: resource.notes || '',
        make: resource.make || '',
        plate: resource.plate || '',
        driver: resource.driver || '',
        fuelType: resource.fuelType || '',
        propMaster: resource.propMaster || '',
        condition: resource.condition || '',
        acquisitionDate: resource.acquisitionDate || '',
        value: resource.value || '',
        setDecorator: resource.setDecorator || '',
        scene: resource.scene || '',
        requirements: resource.requirements || '',
        vendor: resource.vendor || '',
        meals: resource.meals || '',
        dietaryRestrictions: resource.dietaryRestrictions || '',
        contact: resource.contact || '',
        tags: resource.tags || []
      });
      setSelectedType(resource.type || '');
    }
  }, [resource]);

  const handleTypeChange = (event) => {
    const type = event.target.value;
    setSelectedType(type);
    setFormData(prev => ({
      ...prev,
      type: type,
      subtype: '',
    }));
  };

  const handleSubtypeChange = (event) => {
    const subtype = event.target.value;
    setFormData(prev => ({
      ...prev,
      subtype: subtype,
      name: selectedType === 'Custom' ? prev.name : subtype
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for status field
    if (name === 'status') {
      // If not a superuser and trying to change from accepted/denied status, prevent it
      if (!isSuperuser && 
          (resource?.status === 'accepted' || resource?.status === 'denied') && 
          resource?.status !== value) {
        console.log('Only superusers can change status from accepted/denied');
        return; // Don't update the status
      }
    }
    // Date validations
    setFormData(prev => {
      // Prevent endDate before startDate
      if (name === 'endDate' && value < prev.startDate) {
        return prev;
      }
      // If startDate moves after endDate, align endDate
      if (name === 'startDate' && value > prev.endDate) {
        return { ...prev, startDate: value, endDate: value };
      }
      // Time validations when not all-day and same day
      if (!prev.allDay && prev.startDate === prev.endDate) {
        if (name === 'endTime' && prev.startTime && value < prev.startTime) {
          return { ...prev, endTime: prev.startTime };
        }
        if (name === 'startTime' && prev.endTime && value > prev.endTime) {
          return { ...prev, startTime: value, endTime: value };
        }
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedResource = {
        ...formData
        // Use the status from formData (user's selection) without any overrides
      };
      
      let serverResponse;
      if (resource && resource._id) {
        console.log('ResourceEditModal updating resource:', {
          projectId,
          resourceId: resource._id,
          updatedResource
        });
        serverResponse = await resourceService.updateResource(projectId, resource._id, updatedResource);
        console.log('ResourceEditModal resource update successful, server response:', serverResponse);
      } else {
        console.log('ResourceEditModal creating new resource:', {
          projectId,
          updatedResource
        });
        serverResponse = await resourceService.createResource(projectId, updatedResource);
        console.log('ResourceEditModal resource creation successful, server response:', serverResponse);
      }
      
      // Unwrap responses like { resource: {...} }
      const payload = serverResponse && serverResponse.resource ? serverResponse.resource : serverResponse;
      // Ensure payload carries projectId for downstream consumers
      const normalizedResponse = {
        ...payload,
        projectId: payload?.projectId || projectId
      };

      // Broadcast update so ResourceManagement and other listeners can refresh
      try {
        eventEmitter.emit(EVENTS.RESOURCE_UPDATED, { projectId, resourceId: normalizedResponse?._id });
        localStorage.setItem('resourceUpdateFlag', JSON.stringify({ projectId, timestamp: Date.now() }));
      } catch (e) {
        console.warn('ResourceEditModal: failed to broadcast update event/flag', e);
      }

      console.log('ResourceEditModal calling onResourceUpdate with normalized response:', normalizedResponse);
      onResourceUpdate(normalizedResponse);
      onClose();
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: 1,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      <StyledDialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: '64px'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedType && RESOURCE_DETAILS[selectedType] && (
            <Box component="span" sx={{ mr: 1, fontSize: '1.25rem' }}>
              {RESOURCE_DETAILS[selectedType].icon}
            </Box>
          )}
          <Box component="span">
            {resource && resource._id ? 'Edit Resource' : 'Add New Resource'}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ WebkitTextFillColor: 'initial' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Resource Type Selection */}
          <StyledFormField item xs={12}>
            <FormControl 
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  backgroundColor: 'background.paper',
                  px: 0.5,
                },
                mt: 3
              }}
            >
              <InputLabel id="resource-type-label">Resource Type</InputLabel>
              <Select
                labelId="resource-type-label"
                value={selectedType}
                onChange={handleTypeChange}
                label="Resource Type"
              >
                {Object.keys(RESOURCE_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StyledFormField>

          {/* Subtype or Custom Name */}
          {selectedType && (
            <StyledFormField item xs={12}>
              {selectedType === 'Custom' ? (
                <StyledTextField
                  fullWidth
                  label="Resource Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="resource-subtype-label">Resource Subtype</InputLabel>
                  <Select
                    labelId="resource-subtype-label"
                    value={formData.subtype}
                    onChange={handleSubtypeChange}
                    label="Resource Subtype"
                  >
                    {RESOURCE_TYPES[selectedType].map((subtype) => (
                      <MenuItem key={subtype} value={subtype}>
                        {subtype}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </StyledFormField>
          )}



          {/* Time Mode Selector */}
          <StyledFormField item xs={12}>
            <ToggleButtonGroup
              exclusive
              value={formData.allDay ? 'all_day' : 'time_range'}
              onChange={(e, val) => {
                if (!val) return;
                setFormData(prev => ({
                  ...prev,
                  allDay: val === 'all_day',
                  startTime: val === 'time_range' ? (prev.startTime || '09:00') : prev.startTime,
                  endTime: val === 'time_range' ? (prev.endTime || '17:00') : prev.endTime,
                }));
              }}
              size="small"
            >
              <ToggleButton value="all_day">All day</ToggleButton>
              <ToggleButton value="time_range">Between times</ToggleButton>
            </ToggleButtonGroup>
          </StyledFormField>

          {/* Date Fields */}
          <StyledFormField item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="Start Date"
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              InputLabelProps={{
                shrink: true,
              }}
              required
              sx={{
                '& input::-webkit-calendar-picker-indicator': {
                  filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                }
              }}
            />
          </StyledFormField>

          <StyledFormField item xs={12} sm={6}>
            <StyledTextField
              fullWidth
              label="End Date"
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              InputLabelProps={{
                shrink: true,
              }}
              required
              inputProps={{
                min: formData.startDate
              }}
              sx={{
                '& input::-webkit-calendar-picker-indicator': {
                  filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                }
              }}
            />
          </StyledFormField>

          {/* Time Fields (visible when Between times) */}
          {!formData.allDay && (
            <>
              <StyledFormField item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  required
                />
              </StyledFormField>
              <StyledFormField item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  label="End Time"
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300, min: formData.startTime }}
                  required
                />
              </StyledFormField>
            </>
          )}

          {/* Status Selection */}
          <StyledFormField item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="pending_approval">
                  <Chip 
                    label="Pending Approval" 
                    size="small" 
                    color="secondary"
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
                <MenuItem value="accepted">
                  <Chip 
                    label="Accepted" 
                    size="small" 
                    color="success"
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
                <MenuItem value="denied">
                  <Chip 
                    label="Denied" 
                    size="small" 
                    color="error"
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
                <MenuItem value="maintenance">
                  <Chip 
                    label="Maintenance" 
                    size="small" 
                    color="warning"
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </StyledFormField>

          {/* Dynamic Fields Based on Resource Type */}
          {selectedType && RESOURCE_DETAILS[selectedType].fields.map(field => (
            <StyledFormField item xs={12} sm={6} key={field}>
              <StyledTextField
                fullWidth
                label={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                placeholder={`Enter ${field.toLowerCase().replace(/([A-Z])/g, ' $1')}`}
              />
            </StyledFormField>
          ))}

          {/* Notes Field */}
          <StyledFormField item xs={12}>
            <StyledTextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Additional details about the resource..."
            />
          </StyledFormField>
        </Grid>
      </StyledDialogContent>

      <StyledDialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          startIcon={resource && resource._id ? <EditIcon /> : <AddIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: 0.5,
            padding: theme => theme.spacing(1.25, 3),
            borderRadius: theme => theme.shape.borderRadius
          }}
        >
          {resource && resource._id ? 'Update Resource' : 'Add Resource'}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ResourceEditModal;
